/**
 * Decision Engine Guide Tab
 *
 * Provides comprehensive documentation on how the Decision Engine works
 * and guides users through the setup process.
 */

import { PageController } from '@client/components/_core/layout/PageController';
import { Space } from '@client/components/_ui/layout/Space';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Card, Collapse, Divider, Table, Tag, Typography } from 'antd';
import {
    AlertTriangle,
    ArrowRight,
    BarChart3,
    BookOpen,
    BrainCircuit,
    CheckCircle2,
    FileSearch,
    FileText,
    Flag,
    Gauge,
    HelpCircle,
    Lightbulb,
    ListChecks,
    Scale,
    Settings,
    Shield,
    Zap,
} from 'lucide-react';

const { Text, Paragraph } = Typography;

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/$productTypeId/decision-engine/guide',
)({
    staticData: {
        metadata: {
            title: 'Guide',
        },
    },
    component: GuideTab,
});

// Pipeline steps data
const pipelineSteps = [
    {
        title: 'Load Claim Data',
        description: 'Retrieves the claim submission and form data',
        icon: <FileSearch className="size-4" />,
        type: 'system',
    },
    {
        title: 'Load Configuration',
        description: 'Fetches your org-specific decision rules',
        icon: <Settings className="size-4" />,
        type: 'system',
    },
    {
        title: 'Extract Key Information',
        description: 'AI parses the claim narrative, extracts facts, and detects behavioural signals',
        icon: <BrainCircuit className="size-4" />,
        type: 'ai',
    },
    {
        title: 'Classify Loss Cause',
        description: 'AI matches claim to your loss cause taxonomy',
        icon: <ListChecks className="size-4" />,
        type: 'ai',
    },
    {
        title: 'Evaluate Coverage',
        description: 'Determines coverage status and decision pathway',
        icon: <Shield className="size-4" />,
        type: 'system',
    },
    {
        title: 'Evaluate PSD Compliance',
        description: 'AI checks claim against policy document sections and exclusions',
        icon: <FileText className="size-4" />,
        type: 'ai',
    },
    {
        title: 'Detect Red Flags',
        description: 'AI + rules identify potential warning indicators',
        icon: <Flag className="size-4" />,
        type: 'ai',
    },
    {
        title: 'Evaluate Fast-Track',
        description: 'Checks eligibility for accelerated processing',
        icon: <Zap className="size-4" />,
        type: 'system',
    },
    {
        title: 'Calculate Confidence Offsets',
        description: 'Applies trust signals that adjust risk score',
        icon: <Scale className="size-4" />,
        type: 'system',
    },
    {
        title: 'Evaluate Policy Limits',
        description: 'Checks against sub-limits and aggregates',
        icon: <Gauge className="size-4" />,
        type: 'system',
    },
    {
        title: 'Build Assessment',
        description: 'Compiles final risk level and recommendations',
        icon: <BarChart3 className="size-4" />,
        type: 'system',
    },
    {
        title: 'Save Results',
        description: 'Persists assessment to claim record',
        icon: <CheckCircle2 className="size-4" />,
        type: 'system',
    },
];

// Troubleshooting data
const troubleshootingData = [
    {
        key: '1',
        issue: 'Claims not being classified correctly',
        cause: 'Loss cause taxonomy may be too broad or missing AI hints',
        solution:
            'Add more specific secondary loss causes and provide clear AI hints describing what each category covers',
    },
    {
        key: '2',
        issue: 'Too many false positive red flags',
        cause: 'Red flag detection thresholds may be too sensitive',
        solution: 'Increase the minimum confidence threshold for problematic red flags or adjust severity levels',
    },
    {
        key: '3',
        issue: 'Claims not qualifying for fast-track',
        cause: 'Blocker conditions may be too restrictive',
        solution: 'Review blocker conditions and consider relaxing thresholds for lower-risk categories',
    },
    {
        key: '4',
        issue: 'Risk scores seem too high/low',
        cause: 'Red flag weights or confidence offsets may be miscalibrated',
        solution: 'Review severity weights and offset values; use the Override tab to see which flags are triggering',
    },
    {
        key: '5',
        issue: 'Coverage decisions are incorrect',
        cause: 'Loss causes may not be linked to correct coverage types',
        solution: 'Ensure each loss cause has the correct coverage type mapping in the Loss Causes tab',
    },
    {
        key: '6',
        issue: 'Assessment taking too long',
        cause: 'Large claim narratives or complex form data',
        solution: 'This is normal for detailed claims; assessments typically complete within 10-30 seconds',
    },
    {
        key: '7',
        issue: 'Semantic signals (self-reporting, witness, containment) not being detected',
        cause: 'AI confidence below threshold or unclear narrative phrasing',
        solution:
            'The AI requires clear language in the narrative. Ensure claim descriptions explicitly mention acknowledgements, witnesses, or mitigation actions. The default confidence threshold is 60%.',
    },
    {
        key: '8',
        issue: 'PSD compliance showing "NO_PSD_AVAILABLE"',
        cause: 'No processed Policy Schedule Document uploaded for the product type',
        solution:
            'Upload and process the Policy Schedule Document for this product type. Navigate to the PDS configuration section to upload the policy document.',
    },
    {
        key: '9',
        issue: 'Too many PSD violations or concerns detected',
        cause: 'Policy wording is strict or claim data is incomplete',
        solution:
            'Review the findings carefully to distinguish genuine policy violations from cases where claim information is simply missing. Ensure claim narratives include all required details.',
    },
    {
        key: '10',
        issue: 'PSD compliance assessment taking too long',
        cause: 'Large or complex policy document being processed',
        solution:
            'This is normal for detailed policies. PSD compliance assessments typically complete within 30-60 seconds depending on document complexity.',
    },
];

const troubleshootingColumns = [
    {
        title: 'Issue',
        dataIndex: 'issue',
        key: 'issue',
        width: '25%',
    },
    {
        title: 'Likely Cause',
        dataIndex: 'cause',
        key: 'cause',
        width: '35%',
    },
    {
        title: 'Solution',
        dataIndex: 'solution',
        key: 'solution',
        width: '40%',
    },
];

function GuideTab() {
    return (
        <PageController route={Route}>
            <Space direction="vertical" size="large" className="w-full">
                {/* Header */}
                <div className="flex items-start gap-4">
                    <div>
                        <Text type="secondary">
                            Learn how the AI-powered Decision Engine works and how to configure it for your
                            organisation.
                        </Text>
                    </div>
                </div>

                <Collapse
                    defaultActiveKey={['overview', 'pipeline']}
                    items={[
                        // Section 1: Overview
                        {
                            key: 'overview',
                            label: (
                                <span className="flex items-center gap-2 font-medium">
                                    <HelpCircle className="size-4" />
                                    What is the Decision Engine?
                                </span>
                            ),
                            children: (
                                <Space direction="vertical" size="middle" className="w-full">
                                    <Paragraph>
                                        The Decision Engine is an AI-powered claims assessment system that automatically
                                        analyses incoming claims and provides structured recommendations. It combines
                                        machine learning with your organisation&apos;s specific rules to deliver
                                        consistent, auditable decisions.
                                    </Paragraph>

                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                        <Card
                                            size="small"
                                            className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950"
                                        >
                                            <div className="flex items-start gap-3">
                                                <Zap className="mt-0.5 size-5 text-green-600 dark:text-green-400" />
                                                <div>
                                                    <Text strong>Faster Processing</Text>
                                                    <Paragraph className="!mb-0 text-muted-foreground text-sm">
                                                        Automated triage reduces manual review time by identifying
                                                        straightforward claims for fast-tracking.
                                                    </Paragraph>
                                                </div>
                                            </div>
                                        </Card>

                                        <Card
                                            size="small"
                                            className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950"
                                        >
                                            <div className="flex items-start gap-3">
                                                <Scale className="mt-0.5 size-5 text-blue-600 dark:text-blue-400" />
                                                <div>
                                                    <Text strong>Consistent Decisions</Text>
                                                    <Paragraph className="!mb-0 text-muted-foreground text-sm">
                                                        Applies the same rules to every claim, reducing variability and
                                                        ensuring fair treatment.
                                                    </Paragraph>
                                                </div>
                                            </div>
                                        </Card>

                                        <Card
                                            size="small"
                                            className="border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950"
                                        >
                                            <div className="flex items-start gap-3">
                                                <Shield className="mt-0.5 size-5 text-purple-600 dark:text-purple-400" />
                                                <div>
                                                    <Text strong>Complete Audit Trail</Text>
                                                    <Paragraph className="!mb-0 text-muted-foreground text-sm">
                                                        Every decision is documented with reasoning, supporting
                                                        regulatory compliance.
                                                    </Paragraph>
                                                </div>
                                            </div>
                                        </Card>
                                    </div>

                                    <Alert
                                        type="info"
                                        showIcon
                                        title="When to use the Decision Engine"
                                        description="Enable the Decision Engine for product types where you want automated claim triage, risk scoring, and fast-track eligibility checks. It works best when you have a well-defined loss cause taxonomy."
                                    />
                                </Space>
                            ),
                        },

                        // Section 2: How It Works
                        {
                            key: 'pipeline',
                            label: (
                                <span className="flex items-center gap-2 font-medium">
                                    <BrainCircuit className="size-4" />
                                    How It Works: The 12-Step Pipeline
                                </span>
                            ),
                            children: (
                                <Space direction="vertical" size="middle" className="w-full">
                                    <Paragraph>
                                        When a claim is submitted, the Decision Engine runs a 12-step assessment
                                        pipeline. Each step builds on the previous one to produce a comprehensive risk
                                        assessment.
                                    </Paragraph>

                                    <div className="mb-4 flex flex-wrap gap-2">
                                        <Tag color="blue">
                                            <span className="flex items-center gap-1">
                                                <BrainCircuit className="size-3" />
                                                AI-Powered Step
                                            </span>
                                        </Tag>
                                        <Tag color="default">
                                            <span className="flex items-center gap-1">
                                                <Settings className="size-3" />
                                                Rule-Based Step
                                            </span>
                                        </Tag>
                                    </div>

                                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                                        {pipelineSteps.map((step, index) => (
                                            <div
                                                key={index}
                                                className={`flex items-start gap-3 rounded-lg border p-3 ${
                                                    step.type === 'ai'
                                                        ? 'border-accent-200 bg-accent-50'
                                                        : 'border-separator bg-muted'
                                                }`}
                                            >
                                                <div
                                                    className={`flex size-7 items-center justify-center rounded-full font-semibold text-xs ${
                                                        step.type === 'ai'
                                                            ? 'bg-accent-500 text-white'
                                                            : 'bg-gray-500 text-white'
                                                    }`}
                                                >
                                                    {index + 1}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        {step.icon}
                                                        <Text strong className="text-sm">
                                                            {step.title}
                                                        </Text>
                                                    </div>
                                                    <p
                                                        className={`m-0 text-xs ${
                                                            step.type === 'ai'
                                                                ? 'text-accent-700 dark:text-white/80'
                                                                : 'text-muted-foreground dark:text-white/70'
                                                        }`}
                                                    >
                                                        {step.description}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Space>
                            ),
                        },

                        // Section 3: Setup Checklist
                        {
                            key: 'setup',
                            label: (
                                <span className="flex items-center gap-2 font-medium">
                                    <CheckCircle2 className="size-4" />
                                    Setup Checklist
                                </span>
                            ),
                            children: (
                                <Space direction="vertical" size="middle" className="w-full">
                                    <Paragraph>
                                        Follow these steps to configure the Decision Engine for your product type.
                                        Complete them in order for best results.
                                    </Paragraph>

                                    <ol className="list-decimal space-y-4 pl-5">
                                        <li>
                                            <Text strong>Define Loss Causes</Text>
                                            <Paragraph className="!mb-0 mt-1 text-muted-foreground">
                                                Create your taxonomy of loss causes in the{' '}
                                                <Text strong>Loss Causes</Text> tab. Start with primary categories, then
                                                add secondary causes for each. Include AI hints to help classification
                                                accuracy.
                                            </Paragraph>
                                        </li>
                                        <li>
                                            <Text strong>Configure Red Flags</Text>
                                            <Paragraph className="!mb-0 mt-1 text-muted-foreground">
                                                Set up warning indicators in the <Text strong>Red Flags</Text> tab.
                                                Define what patterns should trigger alerts, set severity levels, and
                                                configure minimum confidence thresholds.
                                            </Paragraph>
                                        </li>
                                        <li>
                                            <Text strong>Set Confidence Offsets</Text>
                                            <Paragraph className="!mb-0 mt-1 text-muted-foreground">
                                                Add trust signals in the <Text strong>Confidence Offsets</Text> tab.
                                                These can reduce the risk score when positive indicators are present
                                                (e.g., supporting documentation, verified credentials).
                                            </Paragraph>
                                        </li>
                                        <li>
                                            <Text strong>Define Limits</Text>
                                            <Paragraph className="!mb-0 mt-1 text-muted-foreground">
                                                Configure policy limits and sub-limits in the <Text strong>Limits</Text>{' '}
                                                tab. Set up aggregate tracking for linked claims and warning thresholds.
                                            </Paragraph>
                                        </li>
                                        <li>
                                            <Text strong>Upload PSD Text (Optional)</Text>
                                            <Paragraph className="!mb-0 mt-1 text-muted-foreground">
                                                Upload and process the Policy Schedule Document in the{' '}
                                                <Text strong>PSD</Text> tab. This enables AI-powered compliance checking
                                                against actual policy wording.
                                            </Paragraph>
                                        </li>
                                        <li>
                                            <Text strong>Create Fast-Track Rules</Text>
                                            <Paragraph className="!mb-0 mt-1 text-muted-foreground">
                                                Define eligibility criteria in the <Text strong>Fast-Track Rules</Text>{' '}
                                                tab. Specify which loss causes can be fast-tracked, what blockers
                                                prevent it, and minimum information requirements.
                                            </Paragraph>
                                        </li>
                                        <li>
                                            <Text strong>Enable and Test</Text>
                                            <Paragraph className="!mb-0 mt-1 text-muted-foreground">
                                                Enable the engine in the <Text strong>Overview</Text> tab. Test with
                                                sample claims to verify classifications and risk scores before going
                                                live.
                                            </Paragraph>
                                        </li>
                                    </ol>
                                </Space>
                            ),
                        },

                        // Section 4: Component Details
                        {
                            key: 'components',
                            label: (
                                <span className="flex items-center gap-2 font-medium">
                                    <Settings className="size-4" />
                                    Configuration Components
                                </span>
                            ),
                            children: (
                                <Space direction="vertical" size="middle" className="w-full">
                                    <Collapse
                                        size="small"
                                        items={[
                                            {
                                                key: 'loss-causes',
                                                label: (
                                                    <Text strong>
                                                        <ListChecks className="mr-2 inline size-4" />
                                                        Loss Causes
                                                    </Text>
                                                ),
                                                children: (
                                                    <Space direction="vertical" className="w-full">
                                                        <Paragraph className="!mb-2">
                                                            Loss causes form a hierarchical taxonomy for classifying
                                                            claims. The AI uses this taxonomy along with your AI hints
                                                            to match incoming claims to the appropriate category.
                                                        </Paragraph>
                                                        <ul className="list-inside list-disc space-y-1 text-muted-foreground text-sm">
                                                            <li>
                                                                <Text strong>Primary causes</Text> are broad categories
                                                                (e.g., &quot;Professional Liability&quot;,
                                                                &quot;Property Damage&quot;)
                                                            </li>
                                                            <li>
                                                                <Text strong>Secondary causes</Text> are specific
                                                                sub-types (e.g., &quot;Negligent Advice&quot;,
                                                                &quot;Water Damage&quot;)
                                                            </li>
                                                            <li>
                                                                <Text strong>AI hints</Text> provide context to improve
                                                                classification accuracy
                                                            </li>
                                                            <li>
                                                                <Text strong>Coverage type</Text> links each cause to
                                                                the relevant policy coverage
                                                            </li>
                                                        </ul>
                                                    </Space>
                                                ),
                                            },
                                            {
                                                key: 'red-flags',
                                                label: (
                                                    <Text strong>
                                                        <Flag className="mr-2 inline size-4" />
                                                        Red Flags
                                                    </Text>
                                                ),
                                                children: (
                                                    <Space direction="vertical" className="w-full">
                                                        <Paragraph className="!mb-2">
                                                            Red flags are warning indicators that increase the risk
                                                            score. They can be detected by AI or triggered by rule-based
                                                            conditions.
                                                        </Paragraph>
                                                        <ul className="list-inside list-disc space-y-1 text-muted-foreground text-sm">
                                                            <li>
                                                                <Text strong>Categories:</Text> Timing, Documentation,
                                                                Financial, Behavioural, Pattern, Fraud
                                                            </li>
                                                            <li>
                                                                <Text strong>Severity levels:</Text> Low (4 points),
                                                                Medium (8), High (12), Critical (20)
                                                            </li>
                                                            <li>
                                                                <Text strong>Minimum confidence:</Text> AI must be at
                                                                least this confident to trigger the flag
                                                            </li>
                                                            <li>
                                                                <Text strong>Override capability:</Text> Adjusters can
                                                                override flags with documented reasoning
                                                            </li>
                                                        </ul>
                                                    </Space>
                                                ),
                                            },
                                            {
                                                key: 'confidence-offsets',
                                                label: (
                                                    <Text strong>
                                                        <Scale className="mr-2 inline size-4" />
                                                        Confidence Offsets
                                                    </Text>
                                                ),
                                                children: (
                                                    <Space direction="vertical" className="w-full">
                                                        <Paragraph className="!mb-2">
                                                            Confidence offsets are trust signals that can reduce (or
                                                            occasionally increase) the net risk score based on positive
                                                            or negative factors.
                                                        </Paragraph>
                                                        <ul className="list-inside list-disc space-y-1 text-muted-foreground text-sm">
                                                            <li>
                                                                <Text strong>Negative offsets</Text> reduce risk score
                                                                (e.g., supporting documentation provided: -5)
                                                            </li>
                                                            <li>
                                                                <Text strong>Positive offsets</Text> increase risk score
                                                                (e.g., prior claim history: +3)
                                                            </li>
                                                            <li>
                                                                <Text strong>Range:</Text> Typically -30 to +10, with
                                                                most trust signals being negative (reducing risk)
                                                            </li>
                                                            <li>
                                                                <Text strong>Triggers:</Text> AI-detected semantic
                                                                signals or evidence-based conditions
                                                            </li>
                                                        </ul>
                                                        <Paragraph className="!mt-3 !mb-0 text-sm">
                                                            <Text strong>AI Semantic Signals:</Text> The engine uses AI
                                                            to detect behavioural patterns in the claim narrative:
                                                        </Paragraph>
                                                        <ul className="!mt-1 list-inside list-disc space-y-1 text-muted-foreground text-sm">
                                                            <li>
                                                                <Text strong>Self-reporting</Text> - Claimant
                                                                acknowledges their own error or mistake
                                                            </li>
                                                            <li>
                                                                <Text strong>Witness involvement</Text> - Third-party
                                                                verification or independent witnesses present
                                                            </li>
                                                            <li>
                                                                <Text strong>Immediate containment</Text> - Prompt
                                                                action taken to mitigate damage or loss
                                                            </li>
                                                        </ul>
                                                        <Text type="secondary" className="!mt-2 text-xs">
                                                            These signals are detected with confidence scores. Only
                                                            signals meeting the minimum confidence threshold (default
                                                            60%) are used.
                                                        </Text>
                                                    </Space>
                                                ),
                                            },
                                            {
                                                key: 'limits',
                                                label: (
                                                    <Text strong>
                                                        <Gauge className="mr-2 inline size-4" />
                                                        Limits
                                                    </Text>
                                                ),
                                                children: (
                                                    <Space direction="vertical" className="w-full">
                                                        <Paragraph className="!mb-2">
                                                            Limits define policy coverage limits and sub-limits that
                                                            apply to claims. The engine tracks aggregate usage across
                                                            linked claims.
                                                        </Paragraph>
                                                        <ul className="list-inside list-disc space-y-1 text-muted-foreground text-sm">
                                                            <li>
                                                                <Text strong>Policy limit:</Text> Maximum coverage
                                                                amount for the policy
                                                            </li>
                                                            <li>
                                                                <Text strong>Sub-limits:</Text> Lower limits for
                                                                specific coverage types (e.g., cyber: $500k within $2m
                                                                policy)
                                                            </li>
                                                            <li>
                                                                <Text strong>Aggregate tracking:</Text> Tracks total
                                                                exposure across linked claims in the same occurrence
                                                            </li>
                                                            <li>
                                                                <Text strong>Warning thresholds:</Text> Alerts when
                                                                approaching limit exhaustion
                                                            </li>
                                                        </ul>
                                                    </Space>
                                                ),
                                            },
                                            {
                                                key: 'psd-compliance',
                                                label: (
                                                    <Text strong>
                                                        <FileText className="mr-2 inline size-4" />
                                                        PSD Compliance
                                                    </Text>
                                                ),
                                                children: (
                                                    <Space direction="vertical" className="w-full">
                                                        <Paragraph className="!mb-2">
                                                            PSD (Policy Schedule Document) compliance checking uses AI
                                                            to assess claims against the actual policy wording,
                                                            identifying potential violations, concerns, and
                                                            requirements.
                                                        </Paragraph>
                                                        <ul className="list-inside list-disc space-y-1 text-muted-foreground text-sm">
                                                            <li>
                                                                <Text strong>Processed PSD text:</Text> The policy
                                                                document must be uploaded and processed for compliance
                                                                checking to work
                                                            </li>
                                                            <li>
                                                                <Text strong>Compliance status:</Text> Returns
                                                                COMPLIANT, NON_COMPLIANT, REQUIRES_REVIEW, or
                                                                NO_PSD_AVAILABLE
                                                            </li>
                                                            <li>
                                                                <Text strong>Finding types:</Text> VIOLATION (policy
                                                                breach), CONCERN (potential issue), REQUIREMENT
                                                                (condition to meet), INFO (informational note)
                                                            </li>
                                                            <li>
                                                                <Text strong>Severity levels:</Text> CRITICAL (coverage
                                                                exclusion), HIGH (escalation needed), MEDIUM/LOW (minor
                                                                issues)
                                                            </li>
                                                            <li>
                                                                <Text strong>Section citations:</Text> Each finding
                                                                includes the exact policy section and wording that
                                                                triggered it
                                                            </li>
                                                        </ul>
                                                    </Space>
                                                ),
                                            },
                                            {
                                                key: 'fast-track',
                                                label: (
                                                    <Text strong>
                                                        <Zap className="mr-2 inline size-4" />
                                                        Fast-Track Rules
                                                    </Text>
                                                ),
                                                children: (
                                                    <Space direction="vertical" className="w-full">
                                                        <Paragraph className="!mb-2">
                                                            Fast-track rules define which claims can bypass detailed
                                                            investigation and proceed directly to resolution.
                                                        </Paragraph>
                                                        <ul className="list-inside list-disc space-y-1 text-muted-foreground text-sm">
                                                            <li>
                                                                <Text strong>Loss cause eligibility:</Text> Which loss
                                                                causes qualify for fast-track processing
                                                            </li>
                                                            <li>
                                                                <Text strong>Blocker conditions:</Text> Conditions that
                                                                prevent fast-track (e.g., red flag score &gt; 10)
                                                            </li>
                                                            <li>
                                                                <Text strong>Minimum info required:</Text> What
                                                                information must be present (incident date, summary,
                                                                etc.)
                                                            </li>
                                                            <li>
                                                                <Text strong>Velocity checks:</Text> Blocks fast-track
                                                                if claim frequency exceeds threshold
                                                            </li>
                                                        </ul>
                                                    </Space>
                                                ),
                                            },
                                        ]}
                                    />
                                </Space>
                            ),
                        },

                        // Section 5: Output Interpretation
                        {
                            key: 'output',
                            label: (
                                <span className="flex items-center gap-2 font-medium">
                                    <BarChart3 className="size-4" />
                                    Understanding Assessment Output
                                </span>
                            ),
                            children: (
                                <Space direction="vertical" size="middle" className="w-full">
                                    <Paragraph>
                                        Each assessment produces a structured output that guides claim handling
                                        decisions.
                                    </Paragraph>

                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <Card size="small" title="Risk Level">
                                            <Space direction="vertical" className="w-full">
                                                <div className="flex items-center gap-2">
                                                    <Tag color="success">LOW</Tag>
                                                    <Text type="secondary" className="text-xs">
                                                        Score 0-10: Minimal concerns
                                                    </Text>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Tag color="warning">MEDIUM</Tag>
                                                    <Text type="secondary" className="text-xs">
                                                        Score 11-25: Some flags present
                                                    </Text>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Tag color="orange">HIGH</Tag>
                                                    <Text type="secondary" className="text-xs">
                                                        Score 26-50: Significant concerns
                                                    </Text>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Tag color="error">CRITICAL</Tag>
                                                    <Text type="secondary" className="text-xs">
                                                        Score 51+: Major investigation needed
                                                    </Text>
                                                </div>
                                            </Space>
                                        </Card>

                                        <Card size="small" title="Decision Pathway">
                                            <Space direction="vertical" className="w-full">
                                                <div className="flex items-center gap-2">
                                                    <Tag color="green">ACCEPT_DEFEND</Tag>
                                                    <Text type="secondary" className="text-xs">
                                                        Covered, proceed normally
                                                    </Text>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Tag color="blue">INVESTIGATE</Tag>
                                                    <Text type="secondary" className="text-xs">
                                                        Needs further investigation
                                                    </Text>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Tag color="orange">COVERAGE_REVIEW</Tag>
                                                    <Text type="secondary" className="text-xs">
                                                        Coverage determination required
                                                    </Text>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Tag color="red">DECLINE</Tag>
                                                    <Text type="secondary" className="text-xs">
                                                        Likely excluded from coverage
                                                    </Text>
                                                </div>
                                            </Space>
                                        </Card>
                                    </div>

                                    <Divider className="!my-3" />

                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <Text strong className="mb-2 flex items-center gap-2">
                                                <Flag className="size-4 text-status-danger" />
                                                Red Flag Score Breakdown
                                            </Text>
                                            <Text type="secondary" className="text-sm">
                                                The total red flag score is the sum of all detected flag weights.
                                                Overridden flags are excluded from the total. The breakdown shows which
                                                categories contributed most to the score.
                                            </Text>
                                        </div>
                                        <div>
                                            <Text strong className="mb-2 flex items-center gap-2">
                                                <Zap className="size-4 text-status-warning" />
                                                Fast-Track Eligibility
                                            </Text>
                                            <Text type="secondary" className="text-sm">
                                                Shows whether the claim qualifies for fast-track processing. If not
                                                eligible, lists the specific blockers preventing it and any missing
                                                information.
                                            </Text>
                                        </div>
                                    </div>

                                    <Divider className="!my-3" />

                                    <div>
                                        <Text strong className="mb-2 flex items-center gap-2">
                                            <FileText className="size-4 text-accent-600" />
                                            PSD Compliance Findings
                                        </Text>
                                        <Text type="secondary" className="text-sm">
                                            When a processed Policy Schedule Document is available, the assessment
                                            includes compliance findings. Each finding shows the type (Violation,
                                            Concern, Requirement, or Info), severity level, and cites the exact policy
                                            section and wording that triggered it. This helps handlers quickly identify
                                            policy-specific issues without manually reviewing the full document.
                                        </Text>
                                    </div>
                                </Space>
                            ),
                        },

                        // Section 6: Best Practices
                        {
                            key: 'best-practices',
                            label: (
                                <span className="flex items-center gap-2 font-medium">
                                    <Lightbulb className="size-4" />
                                    Best Practices
                                </span>
                            ),
                            children: (
                                <Space direction="vertical" size="middle" className="w-full">
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <Card size="small">
                                            <div className="flex items-start gap-3">
                                                <div className="rounded bg-muted p-2">
                                                    <ArrowRight className="size-4 text-status-success" />
                                                </div>
                                                <div>
                                                    <Text strong>Start Small, Iterate</Text>
                                                    <Paragraph className="!mb-0 text-muted-foreground text-sm">
                                                        Begin with your most common loss causes and expand gradually.
                                                        Monitor assessment accuracy before adding complexity.
                                                    </Paragraph>
                                                </div>
                                            </div>
                                        </Card>

                                        <Card size="small">
                                            <div className="flex items-start gap-3">
                                                <div className="rounded bg-muted p-2">
                                                    <FileSearch className="size-4 text-accent-600" />
                                                </div>
                                                <div>
                                                    <Text strong>Test Before Production</Text>
                                                    <Paragraph className="!mb-0 text-muted-foreground text-sm">
                                                        Run assessments on historical claims to validate your
                                                        configuration before enabling auto-assessment.
                                                    </Paragraph>
                                                </div>
                                            </div>
                                        </Card>

                                        <Card size="small">
                                            <div className="flex items-start gap-3">
                                                <div className="rounded bg-muted p-2">
                                                    <Gauge className="size-4 text-accent-600" />
                                                </div>
                                                <div>
                                                    <Text strong>Regular Threshold Reviews</Text>
                                                    <Paragraph className="!mb-0 text-muted-foreground text-sm">
                                                        Periodically review red flag thresholds and fast-track criteria
                                                        based on actual claim outcomes.
                                                    </Paragraph>
                                                </div>
                                            </div>
                                        </Card>

                                        <Card size="small">
                                            <div className="flex items-start gap-3">
                                                <div className="rounded bg-muted p-2">
                                                    <BookOpen className="size-4 text-status-warning" />
                                                </div>
                                                <div>
                                                    <Text strong>Document Your Rationale</Text>
                                                    <Paragraph className="!mb-0 text-muted-foreground text-sm">
                                                        Use the description fields to explain why each rule exists. This
                                                        helps future administrators understand your setup.
                                                    </Paragraph>
                                                </div>
                                            </div>
                                        </Card>
                                    </div>

                                    <Alert
                                        type="warning"
                                        showIcon
                                        icon={<AlertTriangle className="size-4" />}
                                        title="Important Reminder"
                                        description="The Decision Engine provides recommendations, not final decisions. All assessments should be reviewed by qualified claims handlers, especially for high-risk or complex claims."
                                    />
                                </Space>
                            ),
                        },

                        // Section 7: Troubleshooting
                        {
                            key: 'troubleshooting',
                            label: (
                                <span className="flex items-center gap-2 font-medium">
                                    <AlertTriangle className="size-4" />
                                    Troubleshooting
                                </span>
                            ),
                            children: (
                                <Table
                                    dataSource={troubleshootingData}
                                    columns={troubleshootingColumns}
                                    pagination={false}
                                    size="small"
                                    className="[&_.ant-table-cell]:text-sm"
                                />
                            ),
                        },
                    ]}
                />
            </Space>
        </PageController>
    );
}
